import { defineNuxtPlugin, useRuntimeConfig, useRouter } from '#imports'

/**
 * Releases DOM references that Google Tag Manager leaves behind in window.dataLayer.
 *
 * GTM's built-in click listeners push an event object carrying a live reference to the
 * clicked element (`gtm.element`, and its siblings for the other auto-event listeners).
 * window.dataLayer is append-only and lives for the whole document, and a DOM node keeps
 * its ancestors reachable through parentNode — so a single click on a product card pins
 * that card's entire page subtree for as long as the tab is open. Measured on
 * home -> product detail page: ~4100 retained DOM nodes and ~800 event listeners per
 * round trip, growing without bound.
 *
 * The element is held twice per entry: directly under `gtm.element`, and captured by the
 * `eventCallback` closure that GTM's "wait for tags" mechanism attaches. Dropping only
 * the first changes nothing, so both have to go.
 *
 * The sweep therefore deletes two things and nothing else:
 *   - any key whose value is a Node that is no longer connected to the document. Such an
 *     element cannot be read meaningfully by a tag any more — its position, classes and
 *     dataset describe a page the user has already left.
 *   - function-valued keys on GTM's own `gtm.*` auto-events. These are the
 *     navigation-resume callbacks; by the time afterEach runs, the navigation they were
 *     guarding has completed, and GTM tests `typeof eventCallback === 'function'` before
 *     calling, so removing one is a no-op for GTM.
 *
 * Entries themselves stay in place, so GTM's own view of the queue is untouched, and
 * pushes made by our own code (`useGtmTracking`) are never modified.
 */
export default defineNuxtPlugin(() => {
    const config = useRuntimeConfig().public
    if (config.gtmEnabled === 'disabled' || !config.gtmContainerId) return

    function pruneDetachedNodes (): void {
        const dataLayer = window.dataLayer
        if (!dataLayer || typeof dataLayer.length !== 'number') return

        for (let i = 0; i < dataLayer.length; i++) {
            const entry = dataLayer[i]
            if (entry == null || typeof entry !== 'object') continue

            const event = (entry as { event?: unknown }).event
            const isGtmAutoEvent = typeof event === 'string' && event.startsWith('gtm.')

            try {
                for (const key of Object.keys(entry)) {
                    const value = (entry as Record<string, unknown>)[key]
                    const isDetachedNode = value instanceof Node && !value.isConnected
                    const isSpentCallback = isGtmAutoEvent && typeof value === 'function'

                    if (isDetachedNode || isSpentCallback) {
                        Reflect.deleteProperty(entry, key)
                    }
                }
            }
            catch {
                // Frozen or proxied entries are not ours to touch.
            }
        }
    }

    useRouter().afterEach(() => {
        // After the navigation, so the leaving page's elements are already detached.
        requestAnimationFrame(pruneDetachedNodes)
    })
})
